import type { Treaty } from '@elysiajs/eden';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import type { ApiClient } from '@/types/api/api-client.type';
import type { ListQuery } from '@/types/api/list-api';

type CallableApi = ((params: any) => any) & {
  get?: (...args: any[]) => any;
  post?: (...args: any[]) => any;
};

export function createCrudHooks<TApi extends CallableApi>(
  api: TApi,
  model: keyof ApiClient,
) {
  type Params = Parameters<TApi>[0];
  type CreateBody = TApi['post'] extends (...args: infer P) => any ? P[0] : never;
  type UpdateBody = ReturnType<TApi>['patch'] extends (...args: infer P) => any
    ? P[0]
    : never;

  type ListResponse = Awaited<ReturnType<NonNullable<TApi['get']>>>;
  type DetailResponse = Awaited<ReturnType<ReturnType<TApi>['get']>>;
  type CreateResponse = Awaited<ReturnType<NonNullable<TApi['post']>>>;
  type PatchResponse = Awaited<ReturnType<ReturnType<TApi>['patch']>>;
  type DeleteResponse = Awaited<ReturnType<ReturnType<TApi>['delete']>>;

  const keys = {
    all: [model] as const,
    list: (query?: ListQuery<TApi>) => [model, 'list', query] as const,
    detail: (params: Params) => [model, 'detail', params] as const,
  };

  function useList(query?: ListQuery<TApi>) {
    return useQuery<Treaty.Data<ListResponse>, Treaty.Error<ListResponse>>({
      queryKey: keys.list(query),
      queryFn: async () => {
        const res = await api.get!({ query });
        return res.data;
      },
    });
  }

  function useDetail(params: Params) {
    return useQuery<Treaty.Data<DetailResponse>, Treaty.Error<DetailResponse>>({
      queryKey: keys.detail(params),
      queryFn: async () => {
        const res = await api(params).get();
        return res.data;
      },
    });
  }

  function useCreate() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<
      Treaty.Data<CreateResponse>,
      Treaty.Error<CreateResponse>,
      CreateBody
    >({
      mutationFn: async (body) => {
        const res = await api.post!(body);
        return res.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: keys.all,
        });

        toast.success('Dados criados com sucesso!');
      },
    });
  }

  function useUpdate() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<
      Treaty.Data<PatchResponse>,
      Treaty.Error<PatchResponse>,
      {
        id: string;
        data: UpdateBody;
      }
    >({
      mutationFn: async ({ id, data }) => {
        const res = await api({ id }).patch(data);

        return res.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: keys.all,
        });

        toast.success('Dados atualizados com sucesso!');
      },
    });
  }

  function useDelete() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<Treaty.Data<DeleteResponse>, Treaty.Error<DeleteResponse>, Params>(
      {
        mutationFn: async (params) => {
          const res = await api(params).delete();
          return res.data;
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: keys.all,
          });

          toast.success('Dado removido com sucesso!');
        },
      },
    );
  }

  return {
    keys,
    useList,
    useDetail,
    useCreate,
    useUpdate,
    useDelete,
  };
}
